import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Attachment, DialogServiceProvider, DownloadService, Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import { DeckAPI, FetchAPI, InputAPI, Link, Panel, PanelAPI, Stamp, StampIcon, TabControlAPI } from '@philips-emr/tws-components';
import { Color, LayoutAlign, Size } from '@philips-emr/tws-constants';
import Controller, { OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { generateUUID } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { IndicatorDetailsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { Indicator, IndicatorValueStatus, renderTokenPending } from './indicators-controller';

interface IndicatorAttachment extends Attachment {
  nrSeqIndicatorAttachment: number,
}

@Controller({ fragment: FragmentNames.INDICATOR_DETAILS })
class IndicatorDetailsController extends BaseController {

  @Inject(Aliases.DECK_ATTACHMENTS)
  private deckAttachments: DeckAPI;
  @Inject(Aliases.FETCH_VALUE_BY_COMPENTENCE)
  private fetchValueByCompetence: FetchAPI;
  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;
  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.PANEL_MODAL_ATTACHMENT)
  private panelModalAttachment: PanelAPI;
  @Inject(Aliases.TAB_CONTROL)
  private tabControl: TabControlAPI;

  @OnDynamicContentRender(Aliases.FIELD_ATTACHMENT)
  OnDynamicContentRenderStatusIndicator(event: RenderEvent): void {
    const { data, render } = event;
    const { requiresAttachment } = data as Indicator;
    if (requiresAttachment == 'S') {
      render((): ReactElement => <Stamp color={Color.SAFETY_ORANGE}
        icon={StampIcon.WARNING}
        label={MessageService.translate('provider.requires-attachment-for-update')}
      />);
    }
  }

  @OnDynamicContentRender(Aliases.TOKEN_PENDING)
  onRenderTokenPending(event: RenderEvent): void {
    const { data, render } = event;
    if (data) {
      const { pending } = data as Indicator;
      if (pending == 'S') {
        render((): ReactElement => renderTokenPending());
      }
    }
  }

  @OnDynamicContentRender(Aliases.CARD_VALUES)
  onRederStatusValues(event: RenderEvent): void {
    const { data, render } = event;
    if (data) {
      const { ieTarget, dsTarget } = data as Indicator;
      render((): ReactElement => getStampStatusIndicator(ieTarget, dsTarget));
    }
  }

  @OnDynamicContentRender(Aliases.LINK_INFORM)
  onRederLinkInform(event: RenderEvent): void {
    const { data, render } = event;
    if (data) {
      const { ieTarget, dsTarget, nrSeqIndicatorValue, pending } = data as Indicator;
      if (pending == 'S') {
        render((): ReactElement => <div>
          <Panel className="" uuid={generateUUID()} alignItems={LayoutAlign.END}>
            <Link value={MessageService.translate('provider.inform')} onClick={() => this.openModalValue(nrSeqIndicatorValue)} size={Size.MEDIUM} />
          </Panel>
        </div>);
      } else {
        render((): ReactElement => getStampStatusIndicator(ieTarget, dsTarget));
      }
    }
  }

  @OnMount([Aliases.FETCH_VALUES, Aliases.FETCH_VALUE_BY_COMPENTENCE, Aliases.FETCH_ATTACHMENTS])
  onMountFetchs(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProviderIndicator } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqProviderIndicator });
    api.forceFetch();
  }

  @OnMount([Aliases.INPUT_FILTER, Aliases.INPUT_FILTER_ATTACHMENT])
  onMountFilter(event: MountEvent): void {
    const api = event.target as InputAPI;
    api.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnMount(Aliases.FETCH_MODAL)
  onMountFetchModal(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProviderIndicator } = this.historyService.getCurrentRouteParams();
    const { nrSeqIndicatorValue } = this.context.getBulkData();
    api.setParameters({ nrSeqProviderIndicator, nrSeqIndicatorValue });
    api.forceFetch();
  }

  @OnChange([Aliases.INPUT_FILTER, Aliases.INPUT_FILTER_ATTACHMENT])
  onEditFilter(event: ChangeEvent<string>): void {
    const api = event.target as InputAPI;
    const competence = event.value;
    if (competence.length == 7 || competence.length == 0) {
      if (api.getAlias() == Aliases.INPUT_FILTER) {
        const params = this.fetchValueByCompetence.getParameters();
        this.fetchValueByCompetence.setParameters({ ...params, competence });
        this.fetchValueByCompetence.forceFetch();
        return;
      }
      const paramsAttachment = this.fetchAttachments.getParameters();
      this.fetchAttachments.setParameters({ ...paramsAttachment, competence });
      this.fetchAttachments.forceFetch();
    }
  }

  @OnClose(Aliases.MODAL_VALUE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnClose(Aliases.MODAL_ATTACHMENT)
  onCloseModalAttachments(): void {
    this.panelModalAttachment.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_SEND)
  onClickSend(): void {
    this.panelModalAttachment.setVisible(true);
  }

  @OnCancel(Aliases.FORM_ATTACHMENT)
  onCancelAttachment(): void {
    this.panelModalAttachment.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_VALUE)
  onSubmitFormValue(event: SubmitEvent): void {
    const { record } = event;
    const { nrSeqProviderIndicator } = this.historyService.getCurrentRouteParams();
    const { nrSeqIndicatorValue } = this.context.getBulkData();
    HttpService.post(Endpoint.POST.EDIT_INDICATOR_VALUE, { nrSeqProviderIndicator, nrSeqIndicatorValue }, { body: record }).then(() => {
      this.panelModal.setVisible(false);
      this.fetchValueByCompetence.setParameters({ nrSeqProviderIndicator });
      this.fetchValueByCompetence.forceFetch();
      this.tabControl.setActiveTab('attachments');
      this.tabControl.setActiveTab('values');
    }).catch((error) => {
      DialogServiceProvider.alert(error.body.response);
    });
  }

  @OnCancel(Aliases.FORM_VALUE)
  onCancelFormValue(): void {
    this.panelModal.setVisible(false);
  }

  @OnSelectItem(Aliases.DECK_ATTACHMENTS)
  onSelectAttachment(event: SelectItemEvent<IndicatorAttachment>): void {
    const { nrSeqIndicatorAttachment, filename } = event.item;
    this.downloadAttachment(filename, nrSeqIndicatorAttachment);
    this.deckAttachments.unselectAll();
  }

  @OnSubmit(Aliases.FORM_ATTACHMENT)
  onSubmitFormAttachment(event: SubmitEvent): void {
    const { record } = event;
    const { nrSeqProviderIndicator } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.SAVE_INDICATOR_ATTACHMENT, { nrSeqProviderIndicator }, { body: record }).then(() => {
      this.panelModalAttachment.setVisible(false);
      this.fetchAttachments.setParameters({ nrSeqProviderIndicator });
      this.fetchAttachments.forceFetch();
      this.tabControl.setActiveTab('values');
      this.tabControl.setActiveTab('attachments');
    }).catch((error) => {
      ToastService.open(error);
    });
  }

  private downloadAttachment(filename: string, nrSeqIndicatorAttachment: number): void {
    const { nrSeqProviderIndicator } = this.historyService.getCurrentRouteParams();
    DownloadService.of({
      endpoint: Endpoint.GET.DOWNLOAD_INDICATOR_ATTACHMENT,
      name: filename,
      parameters: { nrSeqProviderIndicator, nrSeqIndicatorAttachment },
    });
  }

  private openModalValue(nrSeqIndicatorValue: number): void {
    this.context.set({ nrSeqIndicatorValue });
    this.panelModal.setVisible(true);
  }
}

function getStampStatusIndicator(ieStatus: IndicatorValueStatus, label: string): ReactElement {
  let stampProps = null;
  const icon = {
    [IndicatorValueStatus.INFORM]: { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label },
    [IndicatorValueStatus.ON_TARGET]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [IndicatorValueStatus.OUT_OF_TARGET]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label },
  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

export {
  IndicatorDetailsController,
  getStampStatusIndicator,
};
